/**
 * 按钮权限工具
 *
 * 权限数据来源于后端 `/v2/user-context/current` 返回的 `buttonPermissions`（Map<alias, Boolean>），
 * 由布局入口（indexView / MainLayout）加载后通过 setButtonPermissions 缓存到本地。
 * 页面通过 v-permission 指令或 hasPermission() 判断按钮是否对当前用户可见。
 */

const BUTTON_PERMISSIONS_KEY = 'aime_button_permissions'

/** 权限数据变化事件，v-permission 指令监听该事件实时刷新按钮显隐 */
export const PERMISSION_CHANGED_EVENT = 'aime:permission-changed'

/** 超级管理员角色位（与后端 RoleTypeEnum 一致） */
const ROLE_TYPE_SUPER_ADMIN = 1
/** 管理员角色位 */
const ROLE_TYPE_ADMIN = 2

/**
 * 读取当前用户按钮权限映射
 * @returns {Object} { [权限编码]: boolean }
 */
export function getButtonPermissions() {
  try {
    return JSON.parse(localStorage.getItem(BUTTON_PERMISSIONS_KEY) || '{}') || {}
  } catch (e) {
    return {}
  }
}

/**
 * 缓存按钮权限映射，并通知 v-permission 指令刷新
 * @param {Object} permissions
 */
export function setButtonPermissions(permissions) {
  if (permissions && typeof permissions === 'object') {
    localStorage.setItem(BUTTON_PERMISSIONS_KEY, JSON.stringify(permissions))
  } else {
    localStorage.removeItem(BUTTON_PERMISSIONS_KEY)
  }
  window.dispatchEvent(new CustomEvent(PERMISSION_CHANGED_EVENT))
}

/**
 * 清理按钮权限缓存（登出时调用），并通知指令刷新
 */
export function clearButtonPermissions() {
  localStorage.removeItem(BUTTON_PERMISSIONS_KEY)
  window.dispatchEvent(new CustomEvent(PERMISSION_CHANGED_EVENT))
}

/**
 * 当前用户是否为超级管理员 / 管理员（二者拥有全部按钮权限）
 */
function isAdminRole() {
  try {
    const userinfo = JSON.parse(localStorage.getItem('userinfo') || '{}') || {}
    if (userinfo.superAdmin === true || userinfo.admin === true) {
      return true
    }
    const roleType = Number(userinfo.roleType || 0)
    return (roleType & ROLE_TYPE_SUPER_ADMIN) > 0 || (roleType & ROLE_TYPE_ADMIN) > 0
  } catch (e) {
    return false
  }
}

/**
 * 判断当前用户是否拥有按钮权限
 * @param {string|string[]} value 权限编码，数组表示满足其一即可（OR 关系）
 * @returns {boolean}
 */
export function hasPermission(value) {
  // 未声明权限编码 → 放行
  if (value === undefined || value === null || value === '') {
    return true
  }
  // 超级管理员 / 管理员 → 全部放行
  if (isAdminRole()) {
    return true
  }
  const permissions = getButtonPermissions()
  const codes = Array.isArray(value) ? value : [value]
  return codes.some((code) => permissions[code] === true)
}
